/*
 * Copyright 2026 North Carolina State University
 */

import { useSyncExternalStore } from 'react';

/**
 * The signed-in user's clock preference — '12h' (2:30 PM) or '24h' (14:30) —
 * chosen on their profile. Every time the app shows or asks for goes through
 * this file, so changing the setting changes it everywhere.
 *
 * Kept as module state rather than only in React context so plain helpers
 * (column formatters, module-level `formatWhen` functions) can use it too.
 * AuthContext sets it whenever the user loads or changes; components that
 * render times call `useTimeFormat()` so they re-render on a change.
 */
let current = '12h';
const listeners = new Set();

export const getTimeFormat = () => current;

export const setTimeFormat = (format) => {
  const next = format === '24h' ? '24h' : '12h';
  if (next === current) return;
  current = next;
  listeners.forEach(listener => listener());
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Re-renders the calling component when the preference changes. */
export const useTimeFormat = () => useSyncExternalStore(subscribe, getTimeFormat);

export const is24Hour = () => current === '24h';

// `hourCycle`, not `hour12: false` — the latter shows midnight as "24:00" in
// some browsers.
const clock = () => ({ hourCycle: current === '24h' ? 'h23' : 'h12' });

const toDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "11/3/2026, 2:30 PM" or "11/3/2026, 14:30". Empty string for no value. */
export const formatDateTime = (value, options = {}) => {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
    ...options,
    ...clock()
  });
};

/** "2:30 PM" or "14:30". */
export const formatTime = (value, options = {}) => {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', ...options, ...clock() });
};

/**
 * Parse a stored wall-clock time — "14:30", "2:30 PM", "02:30 pm" — into
 * { hours (0-23), minutes }. Null when it is not a time (e.g. "N/A").
 */
export const parseClock = (text) => {
  if (typeof text !== 'string') return null;
  const m = text.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])?$/);
  if (!m) return null;
  let hours = Number(m[1]);
  const minutes = Number(m[2]);
  const period = m[3]?.toUpperCase();
  if (period) {
    if (hours < 1 || hours > 12) return null;
    hours = (hours % 12) + (period === 'PM' ? 12 : 0);
  }
  if (hours > 23 || minutes > 59) return null;
  return { hours, minutes };
};

/** A stored wall-clock time in the user's format. Anything unparseable is returned as-is. */
export const formatClock = (text) => {
  const t = parseClock(text);
  if (!t) return text ?? '';
  const mm = String(t.minutes).padStart(2, '0');
  if (current === '24h') return `${String(t.hours).padStart(2, '0')}:${mm}`;
  return `${t.hours % 12 || 12}:${mm} ${t.hours < 12 ? 'AM' : 'PM'}`;
};

/** 12-hour parts ("01"-"12", "AM"/"PM") <-> a 24-hour hour (0-23). */
export const to24Hour = (hour12, period) => (Number(hour12) % 12) + (period === 'PM' ? 12 : 0);
export const from24Hour = (hours) => ({
  hour: String(hours % 12 || 12).padStart(2, '0'),
  period: hours < 12 ? 'AM' : 'PM'
});
